// src/components/guides/GuideViewer.jsx
// The one renderer for Host Playbook guides: a stepper (mirrors the
// WelcomeTour interaction — count, back/next, dots, final CTA) with two
// skins. 'night' matches the missions/plays surfaces (nightStage + GoldCTA)
// so Launch Pad taps feel native to the game; 'light' is standard Mantis for
// the rest of the app. Mobile gets a bottom sheet with internal scroll —
// the WelcomeTour shipped a short-phone clipping bug once; buttons must
// never be cut off. GuideViewerHost mounts ONCE in DashboardLayout: it owns
// the open/close state (via the openGuide window event) plus the floating
// per-route help button, so pages never manage viewer state themselves.

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { NIGHT, nightStage, GoldCTA } from '../../sections/gamification/plays/uiKit';
import { openWelcomeTour } from '../../sections/gamification/WelcomeTour';
import { useAuth } from '../../lib/auth';
import { useSharedGuides } from '../../guides/GuidesContext';
import { visibleSteps } from '../../guides/registry';
import { resolveGuideText } from '../../guides/tokens';
import { onGuideOpen, openGuide, markGuideSeen } from '../../guides/openGuide';
import { guideIcon } from './guideIcons';

const SKINS = {
  night: {
    paperBg: nightStage,
    text: NIGHT.white,
    dim: 'rgba(245,243,255,0.68)',
    kicker: NIGHT.violetSoft,
    dotOn: 'linear-gradient(90deg, #8b5cf6, #22d3ee)',
    dotOff: 'rgba(167,139,250,0.3)'
  },
  light: {
    paperBg: '',
    text: 'text.primary',
    dim: 'text.secondary',
    kicker: 'primary.main',
    dotOn: '',
    dotOff: 'divider'
  }
};

// Markdown-lite for guide bodies: blank-line paragraphs, "- " bullet blocks,
// **bold** inline. Deliberately hand-rolled (no dependency) and closed —
// anything else renders as plain text.
function boldParts(text) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 ? (
      <Box key={i} component="strong" sx={{ fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      part
    )
  );
}

export function RichText({ text, color, fontSize = 14 }) {
  const blocks = String(text || '')
    .split(/\n\n+/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        const isList = lines.every((l) => l.trim().startsWith('- '));
        if (isList) {
          return (
            <Box key={bi} component="ul" sx={{ m: 0, mb: 1.25, pl: 2.5, textAlign: 'left' }}>
              {lines.map((l, li) => (
                <Typography key={li} component="li" sx={{ fontSize, lineHeight: 1.6, color, mb: 0.5 }}>
                  {boldParts(l.trim().slice(2))}
                </Typography>
              ))}
            </Box>
          );
        }
        return (
          <Typography key={bi} sx={{ fontSize, lineHeight: 1.6, color, mb: 1.25, whiteSpace: 'pre-line' }}>
            {boldParts(block)}
          </Typography>
        );
      })}
    </>
  );
}

RichText.propTypes = { text: PropTypes.string, color: PropTypes.any, fontSize: PropTypes.number };

function GuideBody({ guide, skin, onClose }) {
  const { ctx, tokens } = useSharedGuides();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const S = SKINS[skin] || SKINS.light;

  const steps = useMemo(() => visibleSteps(guide, ctx), [guide, ctx]);
  const t = (text) => resolveGuideText(text, tokens);
  const s = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;
  const Icon = guideIcon(guide.icon);

  if (!s) return null;

  const runCta = (cta) => {
    onClose();
    if (cta.route) navigate(cta.route);
    else if (cta.url) window.open(cta.url, '_blank', 'noopener');
    else if (cta.action === 'welcomeTour') openWelcomeTour();
  };

  const isNight = skin === 'night';
  const primaryCtaSx = { px: 4, fontSize: 14 };

  return (
    <Box sx={{ position: 'relative', px: 3, pt: 2, pb: 3, color: S.text }}>
      {/* top row: guide title + step count + close */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Icon sx={{ fontSize: 20, color: S.kicker }} />
        <Typography noWrap sx={{ flexGrow: 1, fontSize: 13, fontWeight: 700, color: S.dim, minWidth: 0 }}>
          {t(guide.title)}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: S.dim, flexShrink: 0 }}>
          {step + 1} / {steps.length}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close guide" sx={{ color: S.dim }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* per-step content, re-keyed so each step slides in (WelcomeTour idiom) */}
      <Box
        key={`${guide.id}-${step}`}
        sx={{
          minHeight: 220,
          animation: 'guideStepIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both',
          '@keyframes guideStepIn': {
            from: { transform: 'translateX(22px)', opacity: 0 },
            to: { transform: 'none', opacity: 1 }
          }
        }}
      >
        {s.media?.type === 'image' ? (
          <Box
            component="img"
            src={s.media.src}
            alt=""
            sx={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 2, mb: 1.5 }}
          />
        ) : null}
        {s.media?.type === 'video' ? (
          <Box component="video" src={s.media.src} controls playsInline sx={{ width: '100%', maxHeight: 260, borderRadius: 2, mb: 1.5 }} />
        ) : null}
        {s.kicker ? (
          <Typography sx={{ fontWeight: 800, fontSize: 11, letterSpacing: '0.3em', textIndent: '0.3em', color: S.kicker }}>
            {t(s.kicker)}
          </Typography>
        ) : null}
        <Typography sx={{ mt: 0.25, mb: 1, fontWeight: 800, fontSize: 20, lineHeight: 1.2, color: S.text }}>{t(s.title)}</Typography>
        <RichText text={t(s.body)} color={S.dim} />
        {!isLast && s.cta ? (
          <Button size="small" variant="text" onClick={() => runCta(s.cta)} sx={{ mt: 0.5, fontWeight: 700 }}>
            {t(s.cta.label)} →
          </Button>
        ) : null}
      </Box>

      {/* dots */}
      {steps.length > 1 ? (
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, mb: 2 }}>
          {steps.map((st, i) => (
            <Box
              key={i}
              sx={{
                width: i === step ? 20 : 8,
                height: 8,
                borderRadius: 99,
                transition: 'all 0.25s ease',
                // Night skin ships raw CSS colors (gradient); light skin uses
                // theme tokens, which only resolve via bgcolor — a token in
                // `background` silently renders transparent.
                ...(isNight ? { background: i === step ? S.dotOn : S.dotOff } : { bgcolor: i === step ? 'primary.main' : 'grey.300' })
              }}
            />
          ))}
        </Stack>
      ) : (
        <Box sx={{ mt: 2 }} />
      )}

      {/* controls */}
      {isLast ? (
        <Stack spacing={1} alignItems="center">
          {s.cta ? (
            isNight ? (
              <GoldCTA onClick={() => runCta(s.cta)} sx={primaryCtaSx}>
                {t(s.cta.label).toUpperCase()}
              </GoldCTA>
            ) : (
              <Button variant="contained" onClick={() => runCta(s.cta)} sx={primaryCtaSx}>
                {t(s.cta.label)}
              </Button>
            )
          ) : null}
          <Button variant="text" onClick={onClose} sx={{ color: S.dim }}>
            Close
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button
            variant="text"
            onClick={() => setStep((v) => Math.max(0, v - 1))}
            sx={{ color: S.dim, visibility: step === 0 ? 'hidden' : 'visible' }}
          >
            Back
          </Button>
          <Button variant="contained" onClick={() => setStep((v) => Math.min(steps.length - 1, v + 1))} sx={{ px: 4, fontWeight: 700 }}>
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
}

GuideBody.propTypes = { guide: PropTypes.object.isRequired, skin: PropTypes.string, onClose: PropTypes.func.isRequired };

export default function GuideViewer({ guide, skin = 'light', onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const S = SKINS[skin] || SKINS.light;
  const paperSx = {
    background: S.paperBg || undefined,
    color: S.text,
    overflowY: 'auto'
  };

  // Bottom sheet on phones (most hosts), centered dialog on bigger screens.
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open
        onClose={onClose}
        PaperProps={{ sx: { ...paperSx, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '92dvh' } }}
      >
        <GuideBody guide={guide} skin={skin} onClose={onClose} />
      </Drawer>
    );
  }
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...paperSx, borderRadius: 4, my: 4, maxHeight: 'calc(100% - 64px)' } }}
    >
      <GuideBody guide={guide} skin={skin} onClose={onClose} />
    </Dialog>
  );
}

GuideViewer.propTypes = { guide: PropTypes.object.isRequired, skin: PropTypes.string, onClose: PropTypes.func };

// Mounted once in DashboardLayout. Owns viewer state + the floating
// per-route help button (bottom-right; only on routes with a matching
// guide, hidden while a guide is open). Fail closed: unknown or gated-out
// guide ids no-op.
export function GuideViewerHost() {
  const { profile } = useAuth() || {};
  const { byId, byAnchor, loading } = useSharedGuides();
  const [openState, setOpenState] = useState(null); // { guideId, skin }
  const location = useLocation();

  useEffect(
    () =>
      onGuideOpen(({ guideId, skin }) => {
        if (guideId) setOpenState({ guideId, skin: skin || 'light' });
      }),
    []
  );

  const guide = openState ? byId(openState.guideId) : null;
  // The dashboard renders at both '/' (index route) and '/dashboard/default';
  // guides anchor the canonical path, so fold the index route onto it.
  const pathname = location.pathname === '/' ? '/dashboard/default' : location.pathname;
  const routeGuide = !loading ? byAnchor(`route:${pathname}`)[0] || null : null;
  // Missions route already lives in the night theme; match it.
  const routeSkin = location.pathname.startsWith('/missions') ? 'night' : 'light';

  const close = () => {
    if (guide) markGuideSeen(profile?.id || null, guide.id);
    setOpenState(null);
  };

  return (
    <>
      {routeGuide && !guide ? (
        <Tooltip title={routeGuide.title} placement="left">
          <Fab
            size="small"
            aria-label={`Help: ${routeGuide.title}`}
            onClick={() => openGuide(routeGuide.id, { skin: routeSkin })}
            sx={{
              position: 'fixed',
              bottom: 18,
              right: 18,
              zIndex: 1200,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper', color: 'primary.main' }
            }}
          >
            <HelpOutlineIcon />
          </Fab>
        </Tooltip>
      ) : null}
      {guide ? <GuideViewer guide={guide} skin={openState.skin} onClose={close} /> : null}
    </>
  );
}
